import { decode as base64Decode, encode as base64Encode } from '@pkg/shared/utils/base64'
import dot from 'dot-object'

export type EncodableId = string | number

export type NextCursorOptions<R, TIE, S> = {
  pageSize?: number
  sortBy?: S
  sortDirection?: 'asc' | 'desc'
  rank?: R
  cursor?: string
  tieBreaker?: TIE
}

export interface DecodedCursor {
  // Secondary sort value (from sortBy)
  id: EncodableId
  // Optional full-text search rank when in search mode
  r?: number
  // Unique tiebreaker value (usually primary key like 'id')
  u?: EncodableId
}

function isDecodedCursor(val: unknown): val is DecodedCursor {
  return (
    typeof val === 'object' && val !== null && 'id' in val && (typeof val.id === 'string' || typeof val.id === 'number')
  )
}

export type Cursor<RANK = 'searchRank', TIE = 'id', SORT = 'createdAt'> = ReturnType<
  typeof createCursor<RANK, TIE, SORT>
>

// remove table name prefix if exists
export function normalizeGetter(val: string): string
export function normalizeGetter(val?: string): string | undefined
export function normalizeGetter(val?: string) {
  if (!val) {
    return val
  }
  return val.replace(/^[a-zA-Z_]+\./, '')
}

export function createCursor<RANK = 'searchRank', TIE = 'id', SORT = 'createdAt'>(
  nextOptsOrCursor: NextCursorOptions<RANK, TIE, SORT> | string | undefined = {},
) {
  const nextOpts =
    typeof nextOptsOrCursor === 'string'
      ? {
          cursor: nextOptsOrCursor,
        }
      : nextOptsOrCursor

  const opts = {
    pageSize: nextOpts.pageSize ?? 20,
    sortBy: (nextOpts.sortBy ?? 'createdAt') as SORT,
    sortDirection: nextOpts.sortDirection ?? 'desc',
    rank: (nextOpts.rank ?? 'searchRank') as RANK,
    tieBreaker: (nextOpts.tieBreaker ?? 'id') as TIE,
  }

  const encode = (payload: DecodedCursor): string | null => {
    return base64Encode(JSON.stringify(payload))
  }

  const next = <T>(rows: T[]): string | null => {
    const { pageSize } = opts
    if (!Array.isArray(rows) || rows.length < pageSize) {
      return null
    }

    const lastIncluded = rows[pageSize - 1]
    if (!lastIncluded) {
      return null
    }

    let sec = dot.pick(normalizeGetter(opts.sortBy as string), lastIncluded, false) as Date | string | number
    // Normalize Date to ISO string; allow bigint/number/string
    if (sec instanceof Date) {
      sec = sec.toISOString()
    }
    if (typeof sec !== 'string' && typeof sec !== 'number') {
      // Unsupported secondary value for cursor
      return null
    }

    let uniq = dot.pick(normalizeGetter(opts.tieBreaker as string), lastIncluded, false) as Date | string | number
    if (uniq instanceof Date) {
      uniq = uniq.toISOString()
    }
    if (typeof uniq !== 'string' && typeof uniq !== 'number') {
      // Fallback: don't include unique tiebreaker if not encodable
      uniq = undefined as unknown as never
    }

    const r = dot.pick(normalizeGetter(opts.rank as string), lastIncluded, false) as number | undefined
    const payload: DecodedCursor = r != null ? { id: sec, r } : { id: sec }
    if (uniq != null) {
      payload.u = uniq
    }
    return encode(payload)
  }

  const decode = (cursor?: string): DecodedCursor | undefined => {
    if (!cursor) {
      return undefined
    }
    try {
      const json = base64Decode(cursor)
      const obj = JSON.parse(json) as DecodedCursor
      if (isDecodedCursor(obj)) {
        return obj
      }
    } catch {
      // ignore invalid cursor
    }
    return undefined
  }

  return {
    ...opts,
    next,
    encode,
    decode,
    current: decode(nextOpts.cursor),
  }
}
